import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { useTranslation } from '../../../contexts/TranslationContext';
import type { Testimonial } from '../types';

interface TestimonialsSectionProps {
  testimonials: Testimonial[];
}

const TestimonialsSection = ({ testimonials }: TestimonialsSectionProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const { t, isLoading: translationsLoading } = useTranslation();
  
  const getTranslation = (key: string, fallback: string): string => {
    if (translationsLoading) return fallback;
    const translation = t(key);
    return translation === key ? fallback : translation;
  };

  // Don't render if no testimonials
  if (!testimonials || testimonials.length === 0) {
    return null;
  }

  // Ensure activeIndex is within bounds
  const safeActiveIndex = Math.min(activeIndex, testimonials.length - 1);
  const activeTestimonial = testimonials[safeActiveIndex];

  const handlePrevious = () => {
    setActiveIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  return (
    <section className="py-16 lg:py-24 bg-muted/30">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full mb-6">
            <Icon name="MessageSquare" size={18} className="text-accent" />
            <span className="text-sm font-medium text-accent">{getTranslation('landing.testimonials.badge', 'Success Stories')}</span>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            {getTranslation('landing.testimonials.headline', 'Voices from Our Community')}
          </h2>
          
          <p className="text-lg text-muted-foreground">
            {getTranslation('landing.testimonials.description', 'Real stories from Tanzanian youth who are making a difference through our platform')}
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          <div className="bg-card border border-border rounded-2xl p-8 lg:p-12 elevation-md">
            <div className="grid lg:grid-cols-[300px,1fr] gap-8 items-center">
              <div className="relative mx-auto lg:mx-0">
                <div className="w-64 h-64 rounded-2xl overflow-hidden elevation-lg">
                  <Image
                    src={activeTestimonial.image}
                    alt={activeTestimonial.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-primary rounded-full flex items-center justify-center elevation-lg">
                  <Icon name="Quote" size={40} className="text-primary-foreground" />
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex gap-1">
                  {[...Array(activeTestimonial.rating)].map((_, i) => (
                    <Icon key={i} name="Star" size={20} className="text-accent fill-accent" />
                  ))}
                </div>

                <blockquote className="text-xl lg:text-2xl text-foreground leading-relaxed font-medium">
                  "{activeTestimonial.quote}"
                </blockquote>

                <div className="space-y-1">
                  <p className="text-lg font-semibold text-foreground">
                    {activeTestimonial.name}
                  </p>
                  <p className="text-muted-foreground">
                    {activeTestimonial.role}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="MapPin" size={16} />
                    <span>{activeTestimonial.location}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={handlePrevious}
              className="w-12 h-12 bg-card border border-border rounded-full flex items-center justify-center hover:bg-primary hover:border-primary hover:text-primary-foreground transition-smooth elevation-sm"
              aria-label="Previous testimonial"
            >
              <Icon name="ChevronLeft" size={20} />
            </button>

            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`w-2 h-2 rounded-full transition-smooth ${
                    index === safeActiveIndex ? 'w-8 bg-primary' : 'bg-border'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              className="w-12 h-12 bg-card border border-border rounded-full flex items-center justify-center hover:bg-primary hover:border-primary hover:text-primary-foreground transition-smooth elevation-sm"
              aria-label="Next testimonial"
            >
              <Icon name="ChevronRight" size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;